/**
 * Authentication Service
 * File: src/services/authService.js
 *
 * Handles login, logout, and session management
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:6006';

const SESSION_KEY = 'waive_session';
const USER_KEY = 'waive_user';

export const authService = {
    /**
     * Login with username and password
     */
    async login(username, password) {
        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password }),
            });

            const data = await response.json();

            if (data.success && data.token) {
                // Store session
                localStorage.setItem(SESSION_KEY, data.token);
                localStorage.setItem(USER_KEY, JSON.stringify(data.user));
            }

            return data;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    },

    /**
     * Logout and clear session
     */
    async logout() {
        const token = localStorage.getItem(SESSION_KEY);

        if (token) {
            try {
                await fetch(`${API_BASE_URL}/api/v1/auth/logout?token=${token}`, {
                    method: 'POST',
                });
            } catch (error) {
                console.error('Logout error:', error);
            }
        }

        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(USER_KEY);
    },

    /**
     * Check if user is logged in
     */
    isLoggedIn() {
        return !!localStorage.getItem(SESSION_KEY);
    },

    /**
     * Get current session token
     */
    getToken() {
        return localStorage.getItem(SESSION_KEY);
    },

    /**
     * Get current user info
     */
    getUser() {
        const userStr = localStorage.getItem(USER_KEY);
        return userStr ? JSON.parse(userStr) : null;
    },

    /**
     * Validate current session with server
     */
    async validateSession() {
        const token = localStorage.getItem(SESSION_KEY);

        if (!token) {
            return { valid: false };
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/auth/validate?token=${token}`);
            const data = await response.json();

            if (!data.valid) {
                // Clear invalid session
                localStorage.removeItem(SESSION_KEY);
                localStorage.removeItem(USER_KEY);
            }

            return data;
        } catch (error) {
            console.error('Session validation error:', error);
            return { valid: false };
        }
    },
};

export default authService;